import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { RichTextEditor } from './rich-text-editor';
import { ContentPreview } from './content-preview';
import { Eye, Edit, Code } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ContentEditorProps {
  value?: string;
  onChange?: (content: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  height?: number;
  disabled?: boolean;
  className?: string;
  showPreview?: boolean;
  showRawHtml?: boolean;
  label?: string;
  description?: string;
  error?: string;
}

export const ContentEditor: React.FC<ContentEditorProps> = ({
  value = '',
  onChange,
  onBlur,
  placeholder = 'Start writing your content...',
  height = 400,
  disabled = false,
  className,
  showPreview = true,
  showRawHtml = false,
  label,
  description,
  error
}) => {
  const [activeTab, setActiveTab] = useState<'edit' | 'preview' | 'html'>('edit');
  const [rawHtml, setRawHtml] = useState(value);

  const handleContentChange = (content: string) => {
    setRawHtml(content);
    if (onChange) {
      onChange(content);
    }
  };

  const handleRawHtmlChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const content = event.target.value;
    setRawHtml(content);
    if (onChange) {
      onChange(content);
    }
  };

  const tabs = [
    { key: 'edit', label: 'Edit', icon: Edit },
    ...(showPreview ? [{ key: 'preview' as const, label: 'Preview', icon: Eye }] : []),
    ...(showRawHtml ? [{ key: 'html' as const, label: 'HTML', icon: Code }] : [])
  ];

  return (
    <div className={cn('space-y-2', className)}>
      {/* Label and Description */}
      {(label || description) && (
        <div className="space-y-1">
          {label && (
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              {label}
            </label>
          )}
          {description && (
            <p className="text-sm text-gray-600">{description}</p>
          )}
        </div>
      )}

      {/* Content Editor Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)} className="w-full">
        <div className="flex items-center justify-between border-b">
          <TabsList className="grid w-auto grid-cols-auto">
            {tabs.map(({ key, label, icon: Icon }) => (
              <TabsTrigger 
                key={key} 
                value={key}
                className="flex items-center gap-2"
              >
                <Icon className="h-4 w-4" />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
          
          {/* Quick Actions */}
          <div className="flex items-center gap-2 px-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                // Clear content
                handleContentChange('');
              }}
              disabled={disabled || !value}
            >
              Clear
            </Button>
          </div>
        </div>

        {/* Edit Tab */}
        <TabsContent value="edit" className="mt-0">
          <RichTextEditor
            value={value}
            onChange={handleContentChange}
            onBlur={onBlur}
            placeholder={placeholder}
            height={height}
            disabled={disabled}
            className="border-0"
          />
        </TabsContent>

        {/* Preview Tab */}
        {showPreview && (
          <TabsContent value="preview" className="mt-0">
            <ContentPreview 
              content={value || rawHtml}
              maxHeight={`${height}px`}
              className="border-t-0"
            />
          </TabsContent>
        )}

        {/* Raw HTML Tab */}
        {showRawHtml && (
          <TabsContent value="html" className="mt-0">
            <textarea
              value={rawHtml}
              onChange={handleRawHtmlChange}
              placeholder="Edit HTML directly..."
              disabled={disabled}
              className={cn(
                'w-full p-4 border border-gray-200 rounded-lg font-mono text-sm resize-vertical',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                disabled && 'bg-gray-50 cursor-not-allowed'
              )}
              style={{ height: `${height}px` }}
            />
          </TabsContent>
        )}
      </Tabs>

      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {/* Content Stats */}
      {value && (
        <div className="flex items-center gap-4 text-xs text-gray-500 pt-2 border-t">
          <span>
            Characters: {value.replace(/<[^>]*>/g, '').length}
          </span>
          <span>
            Words: {value.replace(/<[^>]*>/g, '').split(/\s+/).filter(word => word.length > 0).length}
          </span>
          <span>
            HTML size: {new Blob([value]).size} bytes
          </span>
        </div>
      )}
    </div>
  );
};

export default ContentEditor;